import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import {
  TransactionWaiverFormRule,
  TransactionWaiverFormSchema,
} from "@factory/form/form.rule";
import { transactionWaiverFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useRef } from "react";
import { useTransactionStepContext } from "@domain/transaction/new-transaction/transaction-step.context";
import { useQyGetWaiver } from "@factory/query/waiver.query";
import { GetResponseWaiverModel } from "@factory/model/query-model-waiver";

export function useNewTransactionWaiver() {
  // LOCAL DECLARATION
  const { nextStepper, setSelectedWaiverForm, setSelectedWaiverPreview, signaturePreview } =
    useTransactionStepContext();

  const navigate = useNavigate();
  const { mapCreateTransactionWaiver } = useFormApiMapper();
  const { showError } = useNotificationContext();

  const printWaiverRef = useRef(null);

  // LOCAL FUNCTIONS
  const formMethod = useForm<TransactionWaiverFormSchema>({
    defaultValues: transactionWaiverFormDefault,
    resolver: zodResolver(TransactionWaiverFormRule),
  });
  const { handleSubmit, watch, setValue } = formMethod;
  const selectedWaiver = watch("waiverMeta");

  const handleWaiverSuccess = (response: GetResponseWaiverModel) => {
    const data = response.data ?? [];
    if (data.length === 0) {
      return;
    }
    const firstWaiver = data[0];
    setValue("waiver", firstWaiver.code);
    setValue("waiverMeta", firstWaiver);
  };

  useQyGetWaiver(
    "",
    9999,
    0,
    undefined,
    undefined,
    true,
    handleWaiverSuccess
  );

  const handleValidate = (form: TransactionWaiverFormSchema) => {
    const formData = mapCreateTransactionWaiver(form);
    setSelectedWaiverPreview(form.waiverMeta);
    setSelectedWaiverForm(formData);

    if (!form.waiver) {
      showError("Please select a waiver before you proceed");
      return;
    }
    if (!signaturePreview) {
      showError("Please verify your signature");
      return;
    }
    nextStepper();
  };
  const handleValidateError = (
    err: FieldErrors<TransactionWaiverFormSchema>
  ) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  const handleSetSignature = (file: File | null) => {
    if (!file) {
      return;
    }

    setValue("file", file);
  };

  return {
    formMethod,
    printWaiverRef,
    selectedWaiver,
    handleSetSignature,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
